import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";

// Placeholder testimonials — editable by Sade. Replace `placeholder: true` text with real testimonials.
const TESTIMONIALS = [
  {
    id: 1,
    quote:
      "Working with The Aligned SOULutions transformed how we approach our community programs. Sade didn't just consult — she rolled up her sleeves and built alongside us.",
    name: "Program Director",
    role: "Community wellness nonprofit",
    placeholder: true,
  },
  {
    id: 2,
    quote:
      "The grant proposal Sade developed didn't just secure funding — it sharpened our entire organizational story. We've been using that language ever since.",
    name: "Executive Director",
    role: "Mission-driven organization",
    placeholder: true,
  },
  {
    id: 3,
    quote:
      "Sade brings empathy and execution together in a way I haven't seen before. She makes complex strategy feel human and possible.",
    name: "Founder",
    role: "Small business advisory client",
    placeholder: true,
  },
];

const Testimonials = () => {
  const [idx, setIdx] = useState(0);
  const current = TESTIMONIALS[idx];

  const next = () => setIdx((i) => (i + 1) % TESTIMONIALS.length);
  const prev = () => setIdx((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-24 md:py-32 bg-brand-cream overflow-hidden"
    >
      <div className="absolute top-20 right-0 w-72 h-72 rounded-full bg-brand-periwinkle/30 blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="font-sans text-xs md:text-sm tracking-[0.25em] uppercase text-brand-sage mb-5">
            Voices from the work
          </p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-brand-ink">
            Real partnerships,{" "}
            <span className="italic text-brand-blue">real impact.</span>
          </h2>
        </div>

        <div className="relative bg-white border border-brand-blue/15 rounded-[2rem] p-10 md:p-16 shadow-xl shadow-brand-blue/5">
          <Quote className="absolute -top-6 left-10 w-16 h-16 text-brand-blue/15" strokeWidth={1} />

          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              data-testid={`testimonial-${current.id}`}
            >
              <p className="font-serif font-light text-2xl md:text-3xl leading-[1.4] text-brand-ink">
                "{current.quote}"
              </p>
              <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
                <div>
                  <p className="font-sans font-semibold text-brand-blue">
                    {current.name}
                  </p>
                  <p className="text-sm text-brand-sage mt-0.5">{current.role}</p>
                </div>
                {current.placeholder && (
                  <span className="text-[10px] tracking-[0.2em] uppercase text-brand-sage/60 border border-brand-sage/30 rounded-full px-3 py-1">
                    Sample · Replace with real client
                  </span>
                )}
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-12 flex items-center justify-between">
            <div className="flex gap-2">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  data-testid={`testimonial-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? "w-8 bg-brand-blue" : "w-2 bg-brand-blue/25 hover:bg-brand-blue/50"
                  }`}
                  aria-label={`Go to testimonial ${i + 1}`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={prev}
                data-testid="testimonial-prev"
                className="w-11 h-11 rounded-full border border-brand-blue/30 hover:border-brand-blue hover:bg-brand-blue hover:text-brand-cream text-brand-blue flex items-center justify-center transition-all"
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                data-testid="testimonial-next"
                className="w-11 h-11 rounded-full border border-brand-blue/30 hover:border-brand-blue hover:bg-brand-blue hover:text-brand-cream text-brand-blue flex items-center justify-center transition-all"
                aria-label="Next testimonial"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
